import { FormProvider, useForm } from 'react-hook-form';
import { Stack, Typography } from '@mui/material';
import { initialUserFormDetails, userFormDetailsSchema } from './form-config';
import { useEffect, useMemo } from 'react';

import { JPhoneInput } from '@atoms/JPhoneInput';
import JTextFieldForm from '@atoms/JTextFieldForm';
import Jpaper from '@atoms/JPaper';
import { UserBlockStyled } from '../UserFormRole/user-form-details.styled';
import UserFormDetailsData from '../../models/user-form-details-data';
import UserFormFooter from '../UserFormFooter';
import { useTranslation } from 'react-i18next';
import useUserMutationContext from '../../hooks/useUserMutationContext';
import { yupResolver } from '@hookform/resolvers/yup';

interface UserFormProps {
  includeFooter?: boolean;

  handleChange?: (data: UserFormDetailsData, isDirty: boolean) => void;
}
const UserFormDetails = ({ includeFooter, handleChange }: UserFormProps) => {
  const { t } = useTranslation();
  const { setStepIndex, payload, setPayload, mode, setIsDirty } =
    useUserMutationContext();
  const methods = useForm<UserFormDetailsData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: useMemo(
      () => initialUserFormDetails,
      [initialUserFormDetails],
    ),
    resolver: yupResolver(userFormDetailsSchema(t)),
  });
  const { handleSubmit, setValue, trigger, formState, getValues, watch } =
    methods;
  const { isValid, isDirty } = formState;

  const values = getValues();

  const onSubmit = handleSubmit(() => {
    setStepIndex((prev) => (prev ? prev + 1 : 1));
  });

  useEffect(() => {
    if (isDirty) return;
    // Check if payload is available and update default values accordingly
    if (payload?.userDto) {
      setValue('firstname', payload.userDto.firstname);
      setValue('lastname', payload.userDto.lastname);
      setValue('email', payload.userDto.email);
      setValue('phone', payload.userDto.phone);
      setValue('mobilePhone', payload.userDto.mobilePhone);

      trigger(['firstname', 'lastname', 'email', 'phone', 'mobilePhone']);
    }
  }, [payload, setValue, isDirty]);

  useEffect(() => {
    if (includeFooter) return;

    watch(() => {
      if (handleChange) handleChange(getValues(), isDirty);
    });
  }, [watch, isDirty]);

  useEffect(() => {
    return () => {
      const vals = getValues();
      setPayload((prev) => {
        return {
          userDto: vals,
          userRolesEstablishmentDto: prev?.userRolesEstablishmentDto,
          userRolesEnterpriseDto: prev?.userRolesEnterpriseDto,
        };
      });
    };
  }, [setPayload, getValues]);

  useEffect(() => {
    setIsDirty(isDirty && isValid);
  }, [isDirty, isValid]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} autoComplete="off" noValidate>
        <Stack gap={2}>
          <UserBlockStyled>
            <Jpaper gap={1} alignItems="stretch" attr={{ fullHeight: true }}>
              <Typography variant="h3" mb={3}>
                {t('DETAILS')}
              </Typography>
              <JTextFieldForm
                focused={!!values.firstname}
                required
                name="firstname"
                fullWidth
                noBorder
                label={t('FIRSTNAME')}
              />
              <JTextFieldForm
                focused={!!values.lastname}
                required
                name="lastname"
                fullWidth
                noBorder
                label={t('LASTNAME')}
              />
              {mode === 'create' && (
                <>
                  <JTextFieldForm
                    focused={!!values.email}
                    required
                    name="email"
                    fullWidth
                    noBorder
                    label={t('EMAIL')}
                  />
                  <JPhoneInput
                    value={payload?.userDto.phone}
                    specialLabel={t('PHONE_NUMBER') ?? ''}
                    onChange={(value) => {
                      if (value !== '') setValue('phone', '+' + value);
                      else setValue('phone', null);
                      trigger('phone');
                    }}
                  />
                  <JPhoneInput
                    value={payload?.userDto.mobilePhone}
                    specialLabel={t('PHONE_PRO') ?? ''}
                    onChange={(value) => {
                      if (value !== '') setValue('mobilePhone', '+' + value);
                      else setValue('mobilePhone', null);
                      trigger('mobilePhone');
                    }}
                  />
                </>
              )}
            </Jpaper>
          </UserBlockStyled>
          {includeFooter && (
            <UserFormFooter
              isValid={isValid}
              values={getValues()}
              submitTab={false}
            />
          )}
        </Stack>
      </form>
    </FormProvider>
  );
};

export default UserFormDetails;
